import type { SalesDisplayNotification } from "@/lib/sales-display-notifications";

const DECORATIONS = ["✦", "★", "✧", "●", "✦", "★"];

function initials(name: string): string {
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2);
}

/** Full-bleed interrupt slide for sales, records, goals and birthdays. */
export function DisplayNotificationSlide({ notification }: { notification: SalesDisplayNotification }) {
  const peopleLabel = notification.people.map((person) => person.name).join(" · ");
  const columnCount = Math.min(Math.max(notification.people.length, 1), 3);

  return (
    <div
      data-testid="display-notification-slide"
      className="absolute inset-0 overflow-hidden bg-[var(--sr-reveblaa)] text-white"
    >
      {notification.people.length > 0 ? (
        <div
          className="absolute inset-0 grid"
          style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
        >
          {notification.people.map((person) => (
            <div key={person.id} className="relative min-h-0 overflow-hidden">
              {person.imageUrl ? (
                <img
                  src={person.imageUrl}
                  alt={person.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-white/10 text-8xl font-bold">
                  {initials(person.name)}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        DECORATIONS.map((mark, index) => (
          <span
            key={`${mark}-${index}`}
            className="absolute text-5xl text-white/25"
            style={{ left: `${8 + index * 17}%`, top: `${12 + (index % 2) * 68}%` }}
            aria-hidden
          >
            {mark}
          </span>
        ))
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/20" />
      <div className="relative z-10 flex h-full flex-col justify-end px-16 pb-24 2xl:px-24 2xl:pb-28">
        <p className="text-xl font-semibold uppercase tracking-[0.24em] text-white/75 2xl:text-2xl">
          {notification.kind === "birthday" ? "Vi feirer" : "Siste nytt fra salg"}
        </p>
        <h2 className="mt-4 max-w-6xl text-7xl font-bold tracking-tight 2xl:text-9xl">
          {notification.title}
        </h2>
        <p className="mt-5 max-w-5xl text-3xl font-medium text-white/95 2xl:text-5xl">
          {notification.message}
        </p>
        {notification.kind !== "birthday" && peopleLabel && (
          <p className="mt-5 text-2xl font-semibold text-white/80 2xl:text-3xl">{peopleLabel}</p>
        )}
      </div>
    </div>
  );
}
